<template>
    <teleport to="body">
        <div class="modal-content mx-0 px-2">
            <!-- Header -->
            <div class="modal-header d-flex flex-column align-items-start">
                <h5 class="mb-0">Share Files</h5>
                <p class="text-muted mb-0">Select files to share with <span class="fw-bold">{{ contact.name }}</span>.</p>
                <!-- <button type="button" class="btn-close" @click="closeShareFilesModal"></button> -->
            </div>

            <!-- Loading State -->
            <div v-if="isLoadingMyFiles || isLoadingShares" class="flex-grow-1 d-flex justify-content-center align-items-center my-3">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading files...</span>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="files.length === 0" class="flex-grow-1 emptyStateCard text-center py-4 d-flex flex-column justify-content-center align-items-center">
                <p class="text-muted">Add files to your account before sharing.</p>
                <button class="btn btn-dark" @click="goToFiles">
                    Go to Files
                </button>
            </div>

            <!-- Scrollable File List -->
            <div v-else class="file-list flex-grow-1 px-3">
                <div
                    v-for="file in files" 
                    :key="file.id" 
                    class="file-item border-0 list-group-item d-flex align-items-center py-3 px-0 mx-0 border rounded"
                >
                    <!-- File Icon -->
                    <div class="d-flex align-items-center me-2">
                        <i :class="`${file.icon}`" :style="{ color: file.color }"></i>
                    </div>

                    <!-- File Info -->
                    <div class="d-flex align-items-start flex-column justify-content-center mx-0 flex-grow-1">
                        <h6 class="mb-0 text-sm">{{ file.title }}</h6>
                        <p class="mb-0 text-xs text-muted">{{ file.datetime }} • {{ file.size }}</p>
                    </div>

                    <!-- Action Button (Checkbox or Revoke) -->
                    <div class="d-flex align-items-center">
                        <template v-if="isFileSharedWithContact(file.id)">
                            <button 
                                class="bg-secondary btn btn-xs text-white mx-0 my-0 px-0 w-100"
                                :disabled="isRevoking"
                                @click.stop="revokeAccess(file)"
                            >
                                <span v-if="isRevokingFor(file.id)" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                REVOKE
                            </button>
                        </template>
                        <template v-else>
                            <input 
                                v-model="selectedFiles" 
                                type="checkbox" 
                                :value="file.id"
                                class="mx-0"
                            >
                        </template>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="modal-footer d-flex justify-content-end mt-0">
                <button class="btn btn-outline-shareguard me-2 mb-0" @click="closeShareFilesModal">Cancel</button>
                <button class="btn btn-shareguard mb-0" :disabled="!canShare" @click="handleShare">Share</button>
            </div>
        </div>
    </teleport>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'ShareFilesModal',
    props: {
        contact: {
            type: Object,
            required: true,
            validator: (contact) => contact.email && contact.name
        }
    },
    emits: ['close-share-files-modal'],
    data() {
        return {
            selectedFiles: [],
            isSharing: false,
            isLoadingShares: false,
            isRevoking: false,
            revokingFiles: new Set(),
            localFileShares: new Map()
        };
    },
    computed: {
        ...mapGetters("files", [
            "getShareableFiles",
            "isLoadingShare",
            "isLoadingMyFiles",
            "getFileShares"
        ]),
        files() {
            return this.getShareableFiles;
        },
        canShare() {
            const unsharedSelectedFiles = this.selectedFiles.filter(fileId => !this.isFileSharedWithContact(fileId));
            return unsharedSelectedFiles.length > 0 && !this.isSharing;
        },
        isFileSharedWithContact() {
            return (fileId) => {
                const shares = this.localFileShares.get(fileId) || this.getFileShares(fileId);
                return shares.some(share => share.email === this.contact.email);
            };
        }
    },
    created() {
        this.initializeModal();
        this.$store.commit('setOverlay', true);
    },
    beforeUnmount() {
        this.$store.commit('setOverlay', false);
    },
    methods: {
        ...mapActions("files", [
            "shareFilesAction",
            "fetchMyFilesAction",
            "fetchSharesForFile"
        ]),
        async initializeModal() {
            await this.loadFiles();
            await this.loadAndPreselectSharedFiles();
        },
        async loadFiles() {
            await this.fetchMyFilesAction();
        },
        async loadAndPreselectSharedFiles() {
            this.isLoadingShares = true;
            try {
                const loadSharePromises = this.files.map(async file => {
                    const shares = await this.fetchSharesForFile(file.id);
                    this.localFileShares.set(file.id, shares);
                });
                await Promise.all(loadSharePromises);
                
                // Don't preselect already shared files anymore
                this.selectedFiles = [];
            } catch (error) {
                console.error('Error loading file shares:', error);
            } finally {
                this.isLoadingShares = false;
            }
        },
        closeShareFilesModal() {
            this.$emit('close-share-files-modal');
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        },
        goToFiles() {
            this.$router.push('/myfiles');
            this.closeShareFilesModal();
        },
        async handleShare() {
            if (this.isSharing || this.selectedFiles.length === 0) return;
            
            this.isSharing = true;
            try {
                // Filter out already shared files
                const filesToShare = this.selectedFiles.filter(fileId => !this.isFileSharedWithContact(fileId));
                
                if (filesToShare.length === 0) {
                    this.$store.dispatch('snackbar/showSnackbar', { 
                        title: "No new files to share", 
                        color: "warning" 
                    });
                    return;
                }

                this.closeShareFilesModal();

                // Share files sequentially to avoid race conditions
                for (const fileId of filesToShare) {
                    await this.shareFilesAction({
                        fileIds: [fileId],
                        contact: this.contact
                    });
                    
                    // Update local state immediately after each successful share
                    const currentShares = this.localFileShares.get(fileId) || [];
                    this.localFileShares.set(fileId, [...currentShares, { email: this.contact.email }]);
                }

                // this.$store.dispatch('snackbar/showSnackbar', { 
                //     title: "Files shared successfully!", 
                //     color: "success" 
                // });
                
                // Clear selection and close modal after successful share
                this.selectedFiles = [];
            } catch (error) {
                console.error('Error sharing files:', error);
                this.$store.dispatch('snackbar/showSnackbar', { 
                    title: "Error sharing files!", 
                    color: "danger"
                });
            } finally {
                this.isSharing = false;
            }
        },
        isRevokingFor(fileId) {
            return this.revokingFiles.has(fileId);
        },
        async revokeAccess(file) {
            if (this.isRevoking) return;
            
            this.revokingFiles.add(file.id);
            this.isRevoking = true;
            try {
                await this.shareFilesAction({
                    fileIds: [file.id],
                    contact: this.contact,
                    revoke: true
                });
                
                // Update local state immediately
                const currentShares = this.localFileShares.get(file.id) || [];
                this.localFileShares.set(file.id, currentShares.filter(share => share.email !== this.contact.email));
                
                // this.$store.dispatch('snackbar/showSnackbar', { 
                //     title: `Access revoked for ${file.title}`, 
                //     color: "success" 
                // });
            } catch (error) {
                console.error('Error revoking access:', error);
                this.$store.dispatch('snackbar/showSnackbar', { 
                    title: `Failed to revoke access for ${file.title}`, 
                    color: "danger"
                });
            } finally {
                this.revokingFiles.delete(file.id);
                this.isRevoking = false;
            }
        }
    }
}
</script>

<style scoped>
.modal-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 800px;
  z-index: $zindex-modal-content;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  max-width: 95vw;
}

.modal-header {
  flex-shrink: 0;
  background: white;
  border-bottom: 1px solid #eee;
}

.file-list {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.modal-footer {
  flex-shrink: 0;
  background: white;
  border-top: 1px solid #eee;
  padding-top: 1rem;
  padding-bottom: 0;
}

.spinner-border {
    width: 3rem;
    height: 3rem;
}

.file-item {
    background: white;
    transition: background-color 0.2s;
    border-color: #dee2e6 !important;
}

.file-item:hover {
    background: #f8f9fa;
}

.file-name {
    font-weight: 500;
    margin-bottom: 2px;
}

.file-details {
    font-size: 0.875rem;
}

.form-check-input {
    cursor: pointer;
}

.emptyStateCard {
    border: 2px dotted #dee2e6; /* light gray dotted border */
    border-radius: 12px;
    padding: 32px 24px;
    background-color: #fff;
}

.badge {
    font-size: 0.75rem;
    font-weight: 500;
}

.text-sm {
    font-size: 0.875rem;
}

.text-xs {
    font-size: 0.75rem;
}

/* Button Styles */
.btn {
    min-width: 125px;
    padding: 0.5rem 1rem;
}

/* Checkbox alignment */
input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin-right: 0;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
}

.modal-header p {
  margin-top: 0.5rem;
  margin-bottom: 0;
}
</style>